import { type User } from '@webapp/core/entities/internal/User'
import {
  CardDisplayStatuses,
  type CardDisplayStatusEnum,
} from '@webapp/modules/spending-cards/entities/enums/CardDisplayStatusEnum'
import {
  CardStatuses,
  type CardStatusEnum,
} from '@webapp/modules/spending-cards/entities/enums/CardStatusEnum'
import {
  CardTypes,
  type CardTypeEnum,
} from '@webapp/modules/spending-cards/entities/enums/CardTypeEnum'
import { Cardholder, type CardholderConstructor } from '../Cardholder'
import { IssuingProviders } from '../enums/IssuingProvider'

export interface CardConstructor {
  id: number
  externalId: string
  last4Digits: string
  type: CardTypeEnum
  status: CardStatusEnum
  cardholder: CardholderConstructor
  createdAt: ISODateString
  updatedAt: ISODateString
  requireActivation: boolean
}

/**
 * `Card` entity represents the shape of a spending card as seen by another cardholder.
 */
export class Card {
  id: number
  externalId: string
  last4Digits: string
  type: CardTypeEnum
  status: CardStatusEnum
  cardholder: Cardholder
  createdAt: ISODateString
  updatedAt: ISODateString
  requireActivation: boolean

  constructor(data: CardConstructor) {
    this.id = data.id
    this.externalId = data.externalId
    this.last4Digits = data.last4Digits
    this.type = data.type
    this.status = data.status
    this.cardholder = new Cardholder(data.cardholder)
    this.createdAt = data.createdAt
    this.updatedAt = data.updatedAt
    this.requireActivation = data.requireActivation
  }

  get isStripe(): boolean {
    return this.cardholder.provider === IssuingProviders.STRIPE
  }

  get isAirwallex(): boolean {
    return this.cardholder.provider === IssuingProviders.AIRWALLEX
  }

  get isPhysicalCard(): boolean {
    return this.type === CardTypes.PHYSICAL
  }

  get isVirtualCard(): boolean {
    return this.type === CardTypes.VIRTUAL
  }

  get cardDisplayStatus(): CardDisplayStatusEnum | undefined {
    switch (this.status) {
      case CardStatuses.ACTIVE:
        return CardDisplayStatuses.ACTIVE
      case CardStatuses.INACTIVE:
        if (this.requireActivation) {
          return CardDisplayStatuses.PENDING
        }
        return CardDisplayStatuses.LOCKED
      case CardStatuses.CANCELED:
        return CardDisplayStatuses.CANCELED
      default:
        return CardDisplayStatuses.UNKNOWN
    }
  }

  get isActive(): boolean {
    return this.status === CardStatuses.ACTIVE
  }

  get isPendingActivation(): boolean {
    return this.requireActivation && this.status === CardStatuses.INACTIVE
  }

  get isLocked(): boolean {
    return !this.requireActivation && this.status === CardStatuses.INACTIVE
  }

  get isCanceled(): boolean {
    return this.status === CardStatuses.CANCELED
  }

  get cardholderFullName(): string {
    return `${this.cardholder.firstName} ${this.cardholder.lastName}`
  }
}
